import * as React from 'react';

import { ClickTracker } from 'bernie-analytics';
import { ErrorBoundary } from 'bernie-view';

import { LodgingPwaPropertySearch } from '__generated__/api/types';

import { UitkButtonSize, UitkPrimaryButton } from 'uitk-react-button';
import { UitkIcon } from 'uitk-react-icons';
import { UitkLayoutFlex, UitkLayoutFlexItem } from 'uitk-react-layout-flex';
import { UitkLineSkeleton } from 'uitk-react-skeleton';
import { UitkSpacing } from 'uitk-react-spacing';
import { UitkText } from 'uitk-react-text';

interface PaginationType {
  __typename: 'LodgingPaginationNavigationButtons';
}
type Trigger = NonNullable<LodgingPwaPropertySearch.propertySearch['pagination']>['trigger'];
export type PaginationButtonsTrigger = Extract<Trigger, PaginationType>;
type NextButton = PaginationButtonsTrigger['nextButton'];
type PreviousButton = PaginationButtonsTrigger['previousButton'];

export interface PaginationNavigationProps {
  paginationLabel?: string;
  pagingPrevButton?: NextButton;
  pagingNextButton?: PreviousButton;
  onPrevPage: () => void;
  onNextPage: () => void;
  isLoading: boolean;
}

const ButtonTracked = ClickTracker(UitkPrimaryButton);

export const PaginationNavigation: React.FC<PaginationNavigationProps> = ({
  onPrevPage,
  onNextPage,
  pagingPrevButton,
  pagingNextButton,
  isLoading,
  paginationLabel,
}) => (
  <ErrorBoundary label="hotel.search.paginationNavigationButtons">
    <UitkLayoutFlex data-stid="pagination-navigation" direction="column" justifyContent="center" alignItems="center">
      <UitkSpacing margin={{ blockend: 'four' }}>
        <UitkLayoutFlex space="six" alignItems="center" justifyContent="center">
          <UitkSpacing margin={{ inlineend: 'four' }}>
            <ButtonTracked
              type="button"
              data-stid="previous-button"
              aria-label={pagingPrevButton?.accessibility ?? ''}
              size={UitkButtonSize.MEDIUM}
              disabled={!pagingPrevButton || isLoading || (pagingPrevButton?.disabled ?? false)}
              onClick={onPrevPage}
              rfrr={pagingPrevButton?.action?.analytics?.referrerId ?? ''}
              linkName={pagingPrevButton?.action?.analytics?.linkName ?? ''}
            >
              <UitkIcon name="chevron_left" />
            </ButtonTracked>
          </UitkSpacing>
          {isLoading ? (
            <UitkLayoutFlexItem minWidth={12} shrink={0}>
              <UitkLineSkeleton type="secondary" />
            </UitkLayoutFlexItem>
          ) : (
            paginationLabel && (
              <UitkText size={300} weight="regular">
                {paginationLabel}
              </UitkText>
            )
          )}

          <UitkSpacing margin={{ inlinestart: 'four' }}>
            <ButtonTracked
              type="button"
              data-stid="next-button"
              aria-label={pagingNextButton?.accessibility ?? ''}
              size={UitkButtonSize.MEDIUM}
              disabled={!pagingNextButton || isLoading || (pagingNextButton?.disabled ?? false)}
              onClick={onNextPage}
              rfrr={pagingNextButton?.action?.analytics?.referrerId ?? ''}
              linkName={pagingNextButton?.action?.analytics?.linkName ?? ''}
            >
              <UitkIcon name="chevron_right" />
            </ButtonTracked>
          </UitkSpacing>
        </UitkLayoutFlex>
      </UitkSpacing>
      <UitkText size={100} weight="regular" />
    </UitkLayoutFlex>
  </ErrorBoundary>
);

PaginationNavigation.displayName = 'PaginationNavigation';
